import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getUserPaths } from "../../routes";
import ListItem from "@mui/material/ListItem";
import { useLocation } from "react-router";
import { useMutation } from "react-query";
import AppContext from "../../appContext";
import * as api from "../../services";
import CustomDrawer from "../../Components/CustomDrawer";
import SearchField from "../../Components/SearchField";
import ClientList from "./ClientList";
import Title from "./Title";
import MutationMessage from "../../Components/MutationMessage";
import RenameDialog from "../../Components/RenameDialog";
import ClientMenu from "./ClientMenu";
import { filterOptions } from "./filterOptions";
import useAccountSettings from "../../ManageAccount/useAccountSettings";

const MAKE_ACTIVE = 1;
const ARCHIVE = 2;

export default function Clients() {
  // local state
  const {
    app: {
      openClientDrawer,
      project: { id: projectId },
      sessionId,
      updateClients,
    },
  } = useContext(AppContext);
  const location = useLocation();
  const { t } = useTranslation();
  const [filterOption, setFilterOption] = useState(filterOptions[0]);
  const [clientList, setClientList] = useState([]); // client list
  const [testDevice, setTestDevice] = useState({}); // my device
  const [searchLoading, setSearchLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [client, setClient] = useState(null); // seletced client
  const [openClientMenu, setOpenClientMenu] = useState(null); // more options for client (archive/rename initial)
  const [openDialog, setOpenDialog] = useState({
    open: false,
  });

  // server state
  const searchMutation = useMutation(({ data }) => api.postSearchClient(data), {
    onSuccess: ({ data }) => {
      setClientList(data.clients);
      setSearchLoading(false);
      setTestDevice(data.testDevice);
    },
  });
  const refetchClients = () =>
    searchMutation.mutate({
      data: { ...filterOption, q: searchText, projectId, sessionId },
    });
  const statusMuation = useMutation(
    (statusValue) =>
      api.postStatus({
        clientId: client.id,
        status: statusValue,
        projectId,
        sessionId,
      }),
    { onSuccess: refetchClients }
  );
  const initialMutation = useMutation(
    (title) =>
      api.postInitials({
        clientId: client.id,
        initials: title,
        projectId,
        sessionId,
      }),
    {
      onSuccess: () => {
        refetchClients();
        setOpenDialog((prev) => ({ ...prev, open: false }));
      },
    }
  );
  const { refetchSettings } = useAccountSettings();
  const myDeviceMutation = useMutation(
    () => api.postTestDevice({ clientId: client.id, projectId, sessionId }),
    {
      onSuccess: () => {
        refetchClients(); // display: "this is me" in the clientList
        refetchSettings(); // refetch settings because test device is changed
      },
    }
  );

  const handleFilterItemClick = (option) => setFilterOption(option);

  useEffect(() => {
    // search a client or change client tab
    //setSearchLoading(true);
    setSearchLoading(true);
    const handleSearch = () => {
      searchMutation.mutate({
        data: { ...filterOption, q: searchText, projectId, sessionId },
      });
    };
    if (searchText === "") {
      handleSearch();
    } else {
      const timeOutId = setTimeout(() => {
        handleSearch();
      }, 400);
      return () => {
        clearTimeout(timeOutId);
      };
    }
  }, [searchText, projectId, filterOption, sessionId, updateClients]);

  // client option menu
  const handleCloseClientMenu = () => {
    setOpenClientMenu(null);
  };
  const handleOpenClientMenu = (event, selectedClient) => {
    setOpenClientMenu(event.currentTarget);
    setClient(selectedClient);
  };
  const handleOptionClick = (option) => {
    switch (option.id) {
      case "makeUnActive":
        statusMuation.mutate(ARCHIVE);
        break;

      case "makeActive":
        statusMuation.mutate(MAKE_ACTIVE);
        break;

      case "initials":
        setOpenDialog({ open: true });
        break;

      case "testDevice":
        myDeviceMutation.mutate();
        break;

      default:
        break;
    }
    handleCloseClientMenu();
  };

  return (
    <CustomDrawer
      width={300}
      open={openClientDrawer && getUserPaths.includes(location.pathname)}
      hasTransition={false}
      content={
        <div>
          <Title
            filterOption={filterOption}
            handleFilterItemClick={handleFilterItemClick}
          />
          <ListItem>
            <SearchField
              setUserText={setSearchText}
              placeholder={t("common.search")}
              variant={"searchClient"}
            />
          </ListItem>
          <ClientList
            clients={clientList}
            isLoading={searchLoading}
            testDevice={testDevice}
            handleOpenClientMenu={handleOpenClientMenu}
            refetchClients={refetchClients}
          />
          <ClientMenu
            open={openClientMenu}
            client={client}
            handleClose={handleCloseClientMenu}
            handleOptionClick={handleOptionClick}
          />
          <RenameDialog
            open={openDialog.open}
            setOpen={setOpenDialog}
            renameFunction={(title) => initialMutation.mutate(title)}
            input={client?.initials ?? ""}
            errorFunction={(title) => title.length > 3}
            helperText={t("navigation.clients.initialsDialog.error")}
            dialogTitle={t("navigation.clients.initialsDialog.title")}
            dialogContentText={t("navigation.clients.initialsDialog.text")}
            dialogActionCancel={t("common.actions.cancel")}
            dialogActionConfirm={t("common.actions.ok")}
          />
          <MutationMessage
            mutation={statusMuation}
            text={{
              loading: t("common.notifications.loading"),
              success:
                statusMuation.variables === ARCHIVE
                  ? t("navigation.clients.makeUnActiveSuccess")
                  : t("navigation.clients.makeActiveSuccess"),
            }}
          />
          <MutationMessage
            mutation={initialMutation}
            text={{
              loading: t("common.notifications.loading"),
              success: t("navigation.clients.initialsSuccess"),
            }}
          />
          <MutationMessage
            mutation={myDeviceMutation}
            text={{
              loading: t("common.notifications.loading"),
              success: t("navigation.clients.testDeviceSuccess"),
            }}
          />
        </div>
      }
    />
  );
}
